import { InfoFilled as InfoIcon } from 'components/icons';
import {
    RECORD_BAND_CLASS,
    RECORD_PAGE_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
} from 'components/record-header/RecordHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { skipToken } from '@reduxjs/toolkit/query';
import { useIsAwaitingOneDrive } from 'components/dms/useIsAwaitingOneDrive';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import EmptyState from 'components/EmptyState';
import Loader from 'components/Loader';
import NavigationMenu, { NavigationMenuItem } from 'components/navigation/NavigationMenu';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { SectionEditingResource } from 'components/section-edit/useSectionEditingSession';
import TabsGallery, { TabsGalleryRef, TabType } from 'components/TabsGallery';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { EntityAuditTrailDrawer } from 'pages/audit-trail/entity/EntityAuditTrailDrawer';
import { DelegationAlert } from 'pages/delegations/DelegationAlert';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetMatterBillingSummaryQuery } from 'store/api/matters';
import { FeatureFlag } from 'types/features.ts';
import { MatterStateType } from 'types/matters';
import { cn } from 'utils/cn';
import { isApiMissingRecordError } from 'utils/getApiErrorMessage';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useDrawsOwnLoadFailure } from 'utils/useDrawsOwnLoadFailure';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useMatterWithRestricted } from 'utils/useMatterWithRestricted';
import { useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { MATTER_AUDIT_TRAIL } from './audit-trail/matterAuditTrail';
import { MatterSection } from './form-sections/MatterSection';
import { MatterDraftPageHeader, MatterPageHeader } from './MatterPageHeader';
import { MatterDraftRecordHeader, MatterRecordHeader } from './MatterRecordHeader';
import EffortView from './views/EffortView';
import ReferralsView from './views/ReferralsView';

import AssignmentView from './views/AssignmentView';
import ExpensesView from './views/ExpensesView';
import ExperienceRecordsView from './views/ExperienceRecordsView';
import GeneralView from './views/GeneralView';
import InvoiceView from './views/InvoiceView';
import OtherView from './views/OtherView.tsx';
import PricingView from './views/PricingView';
import TimecardView from './views/TimecardView';

import CustomFieldView from './views/CustomFieldView';
import { ClientAssetsTable } from 'pages/client-assets/ClientAssetsTable';
import { usePartyAssetPermissions } from 'pages/client-assets/usePartyAssetPermissions';
import { DebriefingsView } from 'pages/debriefing/DebriefingsView';
import { useDebriefingPermissions } from 'pages/debriefing/useDebriefingPermissions';
import TasksView from './views/TasksView';

import { CoiCheckScopeType } from 'types/coi-check.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import CoiView from '../coi/views/CoiView.tsx';
import { InsiderListView } from './insider-list/InsiderListView';

const EFFORT_CHILD_COMPONENT_INDEX = 0;
const REFERRAL_CHILD_COMPONENT_INDEX = 1;
const XR_CHILD_COMPONENT_INDEX = 2;
const BD_TAB_ID = 'bd';
const OTHER_TAB_ID = 'other-dropdown';
const PRICING_TAB_ID = 'pricing-and-billing-dropdown';
const READ_INSIDER_LIST = ['READ.MATTER.INSIDER_LIST'];
// The classic dropdown tabs' ids: the new bar has no dropdowns, so each opens its first visible child there.
const OLD_DROPDOWN_TABS = { [BD_TAB_ID]: ['efforts', 'referrals', 'xr'], [OTHER_TAB_ID]: ['other', 'insider-list'] };
const tabsGalleryRef = React.createRef<TabsGalleryRef>();

const MatterPage = (): JSX.Element => {
    const { id: matterId } = useParams();
    const isCaseAssignmentEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const { canRead: canReadDebriefings } = useDebriefingPermissions();
    const { canRead: canReadAssets } = usePartyAssetPermissions();
    const isOfferFlowEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFER_FLOW);
    const isInsiderListEnabled = useIsFeatureEnabled(FeatureFlag.MATTER_INSIDER_LIST);
    const canReadInsiderList = useHasPermissions(READ_INSIDER_LIST);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const isNew = useIsNewDesign();

    const {
        data: matter,
        refetch: refetchMatter,
        isLoading: isEntityLoading,
        error: matterError,
    } = useMatterWithRestricted(matterId);
    // A case out of the viewer's reach is read again as its restricted record, which can fail the same way.
    useDrawsOwnLoadFailure('getMatterById', matterId, !!matter);
    useDrawsOwnLoadFailure('getMatterRestrictedInfo', matterId, !!matter);

    const { data: matterBillingSummary } = useGetMatterBillingSummaryQuery(matterId ?? skipToken, {
        skip: !matter || !!matterError || matter?.isRestricted || matter?.isDraft,
    });
    const leaveDeleted = useLeaveDeletedEntity('/matters');
    const draftActions = useDraftActions({
        draft: matter?.isDraft ? { type: 'case', id: matter.id } : null,
        createRoute: '/matter/new',
        onDeleted: leaveDeleted,
    });

    const {
        classifiers,
        isLoading: isLoadingClassifiers,
        allClassifiers,
    } = useClassifiers({
        codes: [
            'ACCOUNTING_REVENUE_TYPE',
            'ARCHIVING_PROPERTIES_TYPE',
            'MATTER_STATE_TYPE',
            'MATTER_TYPE',
            'MATTER_BLOCKING_POLICY_TYPE',
            'REASON_OFFER_LOST',
            'OFFER_VALUE_TYPE',
            'ENGAGEMENT_LENGTH_UNIT',
            'LANGUAGE',
            'CURRENCY',
            'PRICING_LIMITATION_TYPE',
            'CAP_UNIT_TYPE',
            'INVOICE_TEMPLATE_TYPE',
            'INVOICING_INTERVAL_TYPE',
            'TIMECARD_SPECIFICATION_LAYOUT_TYPE',
            'JOB_ROLE_GROUP_TYPE',
            'EXPENSE_TYPE',
            'EXPENSE_STATUS_TYPE',
            'ASSIGNMENT_STATUS_TYPE',
        ],
    });
    // The new design's General tab shows its practice area for DMS row by the OneDrive answer, so the page asks for
    // it with the case: a card drawn before the answer would move its rows when it lands.
    const isAwaitingOneDrive = useIsAwaitingOneDrive({ skip: !isNew });

    const updateBDMenuChildComponentIndex = (index: number) => {
        tabsGalleryRef.current?.updateChildComponentIndex(BD_TAB_ID, index);
    };

    const bdMenuItems = useMemo<NavigationMenuItem[]>(
        () => [
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(EFFORT_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-efforts'),
            },
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(REFERRAL_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-referrals'),
            },
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(XR_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-experience-records'),
            },
        ],
        []
    );

    // The insider list endpoints answer 403 unless both hold, so its view only mounts then.
    const isInsiderListAvailable = isInsiderListEnabled && canReadInsiderList;

    const otherMenuItems = useMemo<NavigationMenuItem[]>(
        () => [
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(OTHER_TAB_ID, 0),
                title: t('matters.other.title'),
            },
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(OTHER_TAB_ID, 1),
                title: t('matters.insider-list.title'),
            },
        ],
        []
    );

    const pricingMenuItems = useMemo<NavigationMenuItem[]>(
        () => [
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(PRICING_TAB_ID, 0),
                title: t('matters.pricing-and-billing'),
            },
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(PRICING_TAB_ID, 1),
                title: t('client-assets.records'),
            },
        ],
        []
    );

    const { tabs: tabComponents, moreTabs } = useMemo((): { tabs: TabType[]; moreTabs?: TabType[] } => {
        if (!matter || matter.isRestricted) {
            return { tabs: [] };
        }
        const general: TabType = {
            id: 'general',
            label: t('matters.general'),
            component: (
                <GeneralView classifiers={classifiers} matter={matter} matterBillingSummary={matterBillingSummary} />
            ),
        };
        const timecards: TabType = {
            id: 'timecards',
            label: t('matters.timecards'),
            component: <TimecardView matterId={matterId} isInternalClient={matter.clientParty?.isInternalClient} />,
        };
        const expenses: TabType = {
            id: 'expenses',
            label: t('expenses.expenses'),
            component: <ExpensesView matter={matter} classifiers={classifiers} />,
        };
        const assignments: TabType = {
            id: 'assignments',
            label: 'Assignments',
            hidden: !isCaseAssignmentEnabled,
            component: <AssignmentView matter={matter} classifiers={classifiers} />,
        };
        const efforts: TabType = {
            id: 'efforts',
            label: t('bd.bd-efforts'),
            component: <EffortView key="efforts" matterId={matterId} />,
        };
        const referrals: TabType = {
            id: 'referrals',
            label: t('bd.bd-referrals'),
            component: <ReferralsView key="referrals" matterId={matterId} />,
        };
        const experienceRecords: TabType = {
            id: 'xr',
            label: t('bd.bd-experience-records'),
            component: <ExperienceRecordsView key="xr" matterId={matterId} />,
        };
        const tasks: TabType = {
            id: 'tasks',
            label: t('matters.tasks'),
            component: <TasksView matterId={matterId} />,
        };
        const invoices: TabType = {
            id: 'invoices',
            label: t('matters.invoices'),
            component: <InvoiceView matterId={matterId} />,
        };
        const debriefings: TabType = {
            id: 'debriefings',
            label: t('debriefing.debriefings'),
            hidden: !canReadDebriefings,
            component: <DebriefingsView matterId={matterId} />,
        };
        const assets: TabType = {
            id: 'assets',
            label: t('client-assets.tab'),
            hidden: !canReadAssets,
            component: <ClientAssetsTable variant="case-tab" matterId={matterId} />,
        };
        const pricing: TabType = {
            id: 'pricing-and-billing',
            label: t('matters.pricing-and-billing'),
            component: <PricingView classifiers={classifiers} matter={matter} refetchMatter={refetchMatter} />,
        };
        const coi: TabType = {
            id: 'coi',
            label: t('coi.coi'),
            component: <CoiView scope={CoiCheckScopeType.MATTER} entityId={matterId} />,
        };
        const customFields: TabType = {
            id: 'custom-fields',
            label: t('matters.custom-fields.title'),
            hidden: !matter?.customFields?.length,
            component: <CustomFieldView fields={matter.customFields || []} classifiers={allClassifiers} />,
        };
        // The new design edits every custom field in one section, its Edit above the groups.
        const customFieldsSection: TabType = {
            ...customFields,
            label: t('custom-fields.title'),
            component: (
                <MatterSection id="custom-fields" titleId="custom-fields.title" matter={matter}>
                    {(edit) => (
                        <>
                            {edit ? <div className="mb-4 flex justify-end">{edit}</div> : null}
                            {customFields.component}
                        </>
                    )}
                </MatterSection>
            ),
        };
        const other: TabType = {
            id: 'other',
            label: t('matters.other.title'),
            component: <OtherView key="other" matter={matter} classifiers={classifiers} />,
        };
        const insiderList: TabType = {
            id: 'insider-list',
            label: t('matters.insider-list.title'),
            hidden: !isInsiderListAvailable,
            component: <InsiderListView key="insider-list" matterId={matter.id.toString()} />,
        };

        // A draft keeps the tabs of its own fields; those that list or add related records wait for the case.
        if (matter.isDraft) {
            return isNew
                ? { tabs: [general, pricing], moreTabs: [other, customFieldsSection] }
                : { tabs: [general, pricing, customFields, other] };
        }

        // The bar and More, each in order of use. "More" names the menu, so custom fields take their own name.
        if (isNew) {
            return {
                tabs: [general, timecards, invoices, pricing, expenses],
                moreTabs: [
                    referrals,
                    tasks,
                    other,
                    customFieldsSection,
                    efforts,
                    experienceRecords,
                    coi,
                    assignments,
                    debriefings,
                    insiderList,
                    assets,
                ],
            };
        }

        const bdTabs = [efforts, referrals, experienceRecords];
        return {
            tabs: [
                general,
                timecards,
                expenses,
                assignments,
                {
                    id: BD_TAB_ID,
                    label: <NavigationMenu title={t('bd.bd')} menuItems={bdMenuItems} />,
                    displayLabel: <FormattedMessage id="bd.bd" />,
                    isDropdown: true,
                    childTabIds: bdTabs.map((tab) => tab.id),
                    childComponents: bdTabs.map((tab) => tab.component),
                },
                tasks,
                invoices,
                debriefings,
                // A dropdown ignores its children's `hidden`, so without assets pricing stays a plain tab.
                canReadAssets
                    ? {
                          id: PRICING_TAB_ID,
                          label: (
                              <NavigationMenu title={t('matters.pricing-and-billing')} menuItems={pricingMenuItems} />
                          ),
                          displayLabel: t('matters.pricing-and-billing'),
                          isDropdown: true,
                          childTabIds: [pricing.id, assets.id],
                          childComponents: [pricing.component, assets.component],
                      }
                    : pricing,
                coi,
                customFields,
                isInsiderListAvailable
                    ? {
                          id: OTHER_TAB_ID,
                          label: <NavigationMenu title={t('matters.other.title')} menuItems={otherMenuItems} />,
                          displayLabel: t('matters.other.title'),
                          isDropdown: true,
                          childTabIds: [other.id, insiderList.id],
                          childComponents: [other.component, insiderList.component],
                      }
                    : other,
            ],
        };
    }, [
        matter,
        classifiers,
        isCaseAssignmentEnabled,
        canReadDebriefings,
        canReadAssets,
        isInsiderListAvailable,
        matterBillingSummary,
        matterId,
        refetchMatter,
        bdMenuItems,
        otherMenuItems,
        pricingMenuItems,
        allClassifiers,
        isNew,
    ]);

    // A section in edit holds the case's editing session, as the classic edit page does.
    const caseId = matter?.id;
    const editingSession = useMemo<SectionEditingResource | undefined>(
        () =>
            caseId === undefined
                ? undefined
                : { resourceType: 'CASE', resourceId: caseId, messageId: 'matters.concurrent-editing-warning' },
        [caseId]
    );

    const hasOfferOutcome =
        isOfferFlowEnabled &&
        (matter?.matterStateTypeCode === MatterStateType.OFFER_WON ||
            matter?.matterStateTypeCode === MatterStateType.OFFER_LOST);

    if (isEntityLoading || isLoadingClassifiers || isAwaitingOneDrive) {
        return <Loader />;
    }

    // The page itself says the case did not load, and offers the retry: in classic under the error toast, which can
    // be dismissed and would leave a blank page; in the new design alone, in its own words, with no toast over it.
    // Not for a case that is missing or forbidden: the API layer is already sending that one to /404, and a retry
    // could not help it. A case the new design already shows stays when a later reload of it fails: that failure
    // is the toast's.
    if (matterError && !(isNew && matter)) {
        if (isApiMissingRecordError(matterError)) {
            return <></>;
        }
        return (
            <EmptyState
                variant="error"
                size="page"
                errorTitleId={isNew ? 'matters.error.load-title' : 'matters.error-fetching-matter'}
                onRetry={refetchMatter}
                surface={isNew ? 'case-record' : 'matter'}
            />
        );
    }

    const tabsGallery = (
        <TabsGallery
            ref={tabsGalleryRef}
            tabs={tabComponents}
            moreTabs={moreTabs}
            tabAliases={isNew ? OLD_DROPDOWN_TABS : undefined}
            arrows={isNew ? { back: t('back'), next: t('next') } : undefined}
            tabGroupId="matter-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            // The new design's white band runs from the header through the tab strip, edge to edge.
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const uninvoicedAlert = !matter?.isRestricted && hasOfferOutcome && matter?.hasUninvoicedBillableWork && (
        <div className="my-4">
            <DelegationAlert role="status" severity="warning" icon={<InfoIcon className="text-warning" />}>
                <FormattedMessage id="matters.uninvoiced-billable-warning" />
            </DelegationAlert>
        </div>
    );

    const { draft } = draftActions;
    const draftNotice = draft ? (
        <div className={isNew ? 'mt-3.5' : 'mb-4'}>
            <DraftNotice actions={draftActions} />
        </div>
    ) : null;

    return matter ? (
        <DraftActionsContext.Provider value={matter.isDraft ? draftActions : null}>
            <PaddedLayout
                // Bounded only around the tab gallery: the tab panel is then the scroller, so a long
                // list scrolls inside its own table. The restricted view has no panel to scroll, so
                // it stays a plain page that the content area scrolls. The new design's band and panel
                // carry their own gutters, and its page scrolls as one where the band leaves the panel no room.
                className={cn(
                    !matter.isRestricted && 'flex h-full flex-col overflow-hidden',
                    isNew && !matter.isRestricted && RECORD_PAGE_CLASS
                )}
            >
                <div>
                    {isNew ? (
                        <div className={cn(!matter.isRestricted && RECORD_BAND_CLASS)}>
                            {matter.isDraft ? (
                                <MatterDraftRecordHeader
                                    matter={matter}
                                    classifiers={classifiers}
                                    actions={draftActions}
                                />
                            ) : (
                                <MatterRecordHeader
                                    matter={matter}
                                    classifiers={classifiers}
                                    onOpenHistory={() => setIsHistoryOpen(true)}
                                />
                            )}
                            {uninvoicedAlert}
                            {draftNotice}
                        </div>
                    ) : (
                        <>
                            {matter.isDraft ? (
                                <MatterDraftPageHeader
                                    matter={matter}
                                    classifiers={classifiers}
                                    actions={draftActions}
                                />
                            ) : (
                                <MatterPageHeader
                                    matter={matter}
                                    classifiers={classifiers}
                                    onOpenHistory={() => setIsHistoryOpen(true)}
                                />
                            )}
                            {uninvoicedAlert}
                            {draftNotice}
                        </>
                    )}
                </div>

                {matter.isRestricted && (
                    <div className="my-4">
                        <DelegationAlert role="status" severity="warning" icon={<InfoIcon className="text-warning" />}>
                            <div className="text-subtitle1 mb-[0.35em] font-bold">{t('access-restricted')}</div>
                            <div className="text-body2 font-normal">{matter.restrictedAccessMessage}</div>
                        </DelegationAlert>
                    </div>
                )}

                {!matter.isRestricted &&
                    (isNew ? (
                        <SectionEditProvider canEdit={matter.isEditable} session={editingSession}>
                            {tabsGallery}
                        </SectionEditProvider>
                    ) : (
                        tabsGallery
                    ))}
                {/* Keyed by case so filters chosen on one case do not carry over to the next. */}
                {!matter.isRestricted && (
                    <EntityAuditTrailDrawer
                        key={matter.id}
                        trail={MATTER_AUDIT_TRAIL}
                        entityId={matter.id}
                        entityLabel={matter.caseNumber}
                        open={isHistoryOpen}
                        onClose={() => setIsHistoryOpen(false)}
                        detached={isNew}
                    />
                )}
                {draftActions.shareDialog}
            </PaddedLayout>
        </DraftActionsContext.Provider>
    ) : (
        <></>
    );
};

export default MatterPage;
